import { useEffect, useState, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ComparisonTable } from '../components/ComparisonTable';
import { CategoryFilter } from '../components/CategoryFilter';
import { ProductPicker } from '../components/ProductPicker';
import { extractCategories } from '../lib/comparison-utils';
import { ComparisonPowerBars } from '../components/ComparisonPowerBars';
import { useComparison } from '../hooks/useComparison';
import { createComparison, getComparison, type ComparisonResponse, type ProductSearchResult } from '../lib/api';
import type { ComparisonItem } from '@nfa/types';

/**
 * Comparison Page - View and manage product comparisons
 *
 * Supports two modes:
 * 1. Local comparison: Uses products from useComparison hook (localStorage)
 * 2. Saved comparison: Loads from API using ID/share_token from URL
 */
export function Comparison() {
  const { id: urlId } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const shareToken = searchParams.get('share');

  // Use URL ID or share token
  const comparisonId = urlId || shareToken;

  // Local comparison state
  const {
    products: localProducts,
    removeProduct,
    clearComparison,
    canCompare,
    canAddMore,
    getProductIds,
    addProduct,
    productIds: localProductIds,
  } = useComparison();

  // Category filter state
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // State for saved comparison
  const [savedComparison, setSavedComparison] = useState<ComparisonResponse | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Fetch saved comparison from API
  const {
    data: fetchedComparison,
    isLoading: isFetchingComparison,
    error: fetchError,
  } = useQuery({
    queryKey: ['comparison', comparisonId],
    queryFn: () => getComparison(comparisonId!),
    enabled: !!comparisonId,
  });

  // Create comparison mutation
  const createMutation = useMutation({
    mutationFn: createComparison,
    onSuccess: (data) => {
      setSavedComparison(data);
      const url = `${window.location.origin}/compare/${data.slug}`;
      setShareUrl(url);
    },
  });

  // Determine which items to display
  const allItems: ComparisonItem[] = fetchedComparison?.items ?? savedComparison?.items ?? localProducts.map((p) => ({
    product_id: p.id,
    product_name: p.name,
    category_id: p.category_id,
    price_gbp: p.price_gbp,
    weight_g: p.weight_g,
    tier: p.tier,
    features: {},
    cf_image_id: p.cf_image_id,
    image_url: p.image_url,
    image_alt: p.image_alt,
  }));

  // Extract categories for filter
  const categories = useMemo(() => extractCategories(allItems), [allItems]);

  // Filter items by selected category
  const displayItems = useMemo(() => {
    if (!selectedCategory) return allItems;
    return allItems.filter((item) => item.category_id === selectedCategory);
  }, [allItems, selectedCategory]);

  // Handle adding product from picker
  const handleAddFromPicker = (product: ProductSearchResult) => {
    addProduct({
      id: product.id,
      name: product.name,
      category_id: product.category_id,
      price_gbp: product.price_gbp,
      weight_g: product.weight_g,
      tier: product.tier,
      cf_image_id: product.cf_image_id,
      image_url: product.image_url,
      image_alt: product.image_alt,
    });
    // Reset saved state since we modified
    if (savedComparison) {
      setSavedComparison(null);
      setShareUrl(null);
    }
  };

  // Whether viewing a saved comparison (read-only mode)
  const isViewingShared = !!comparisonId && !!fetchedComparison;

  // Handle remove in local mode
  const handleRemove = (productId: string) => {
    if (!isViewingShared) {
      removeProduct(productId);
      // Reset saved state if modified
      if (savedComparison) {
        setSavedComparison(null);
        setShareUrl(null);
      }
    }
  };

  // Handle save/share
  const handleSaveAndShare = () => {
    if (canCompare) {
      createMutation.mutate(getProductIds());
    }
  };

  // Copy share URL to clipboard
  const handleCopyShareUrl = async () => {
    if (shareUrl) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      }
    }
  };

  // Update document title
  useEffect(() => {
    document.title = 'Compare Gear | NFA';
    return () => {
      document.title = 'NFA - Gear Recommendations';
    };
  }, []);

  // Loading state for fetched comparison
  if (comparisonId && isFetchingComparison) {
    return (
      <main className="flex-1 px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500" />
          </div>
        </div>
      </main>
    );
  }

  // Error state for fetched comparison
  if (comparisonId && fetchError) {
    return (
      <main className="flex-1 px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-6xl mx-auto">
          <nav className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Home
            </Link>
          </nav>

          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-8 text-center">
            <h2 className="font-mono text-xl font-bold text-red-400 mb-2">Comparison Not Found</h2>
            <p className="text-neutral-400 mb-4">
              This comparison may have expired or the link is invalid.
            </p>
            <Link
              to="/compare"
              className="inline-block px-4 py-2 bg-orange-500 text-black font-medium rounded-lg hover:bg-orange-400 transition-colors"
            >
              Start a new comparison
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 px-4 sm:px-6 py-8 sm:py-12">
      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Home
          </Link>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-mono text-3xl sm:text-4xl font-bold text-white mb-3">
            Compare Gear
          </h1>
          <p className="text-lg text-neutral-400">
            {isViewingShared
              ? 'Viewing a shared comparison'
              : 'Compare products side-by-side to find the best option for your needs.'}
          </p>
        </div>

        {/* Product Picker (not in shared view, only when can add more) */}
        {!isViewingShared && canAddMore && (
          <div className="mb-6">
            <ProductPicker
              onSelect={handleAddFromPicker}
              excludeIds={localProductIds}
              placeholder="Search and add products to compare..."
            />
          </div>
        )}

        {/* Category Filter (only show with 2+ different categories) */}
        {categories.length > 1 && (
          <div className="mb-6">
            <CategoryFilter
              categories={categories}
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
            />
          </div>
        )}

        {/* Power Bars (only with 2+ items in current view) */}
        {displayItems.length >= 2 && (
          <div className="mb-6">
            <ComparisonPowerBars items={displayItems} />
          </div>
        )}

        {/* Comparison Table */}
        <ComparisonTable
          items={displayItems}
          onRemove={isViewingShared ? undefined : handleRemove}
        />

        {/* Actions */}
        {!isViewingShared && displayItems.length > 0 && (
          <div className="mt-6 flex flex-wrap items-center gap-4">
            {/* Save & Share Button */}
            {canCompare && !savedComparison && (
              <button
                onClick={handleSaveAndShare}
                disabled={createMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-400 text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    Save & Share
                  </>
                )}
              </button>
            )}

            {/* Share URL Display */}
            {shareUrl && (
              <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm text-green-400">Comparison saved!</span>
                <button
                  onClick={handleCopyShareUrl}
                  className="ml-2 px-2 py-1 text-xs bg-neutral-800 hover:bg-neutral-700 text-white rounded transition-colors"
                >
                  {copySuccess ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            )}

            {/* Clear All Button */}
            <button
              onClick={clearComparison}
              className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 hover:text-white font-medium rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Clear All
            </button>
          </div>
        )}

        {/* Error from mutation */}
        {createMutation.isError && (
          <div className="mt-4 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
            <p className="text-red-400 text-sm">
              Failed to save comparison. Please try again.
            </p>
          </div>
        )}

        {/* Browse Products CTA */}
        <div className="mt-12 bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/20 rounded-lg p-6 sm:p-8">
          <h2 className="font-mono text-xl sm:text-2xl font-bold text-white mb-3">
            {displayItems.length === 0
              ? 'Start comparing gear'
              : 'Add more products to compare'}
          </h2>
          <p className="text-neutral-400 mb-6">
            {displayItems.length === 0
              ? 'Browse our gear categories and add products to compare them side-by-side.'
              : 'Browse categories to add more products to your comparison.'}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-black font-bold rounded-lg hover:bg-orange-400 transition-colors"
          >
            <span>Browse Gear</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </div>
    </main>
  );
}
